import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { UsuarioDeSesion } from '@medchoice/shared';
import { api, ApiError, onSessionExpired } from '../api/client';

interface ContextoSesion {
  usuario: UsuarioDeSesion | null;
  cargando: boolean;
  ingresar: (email: string, clave: string) => Promise<UsuarioDeSesion>;
  salir: () => Promise<void>;
}

const Contexto = createContext<ContextoSesion | null>(null);

export function ProveedorSesion({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<UsuarioDeSesion | null>(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let vigente = true;
    api.sesionActual()
      .then((u) => { if (vigente) setUsuario(u); })
      .catch((e: unknown) => {
        // Sin sesión (401) es lo esperable. Cualquier otro error se deja ver en la
        // consola, pero la aplicación arranca igual como visitante.
        if (!(e instanceof ApiError && e.status === 401)) console.error(e);
      })
      .finally(() => { if (vigente) setCargando(false); });

    onSessionExpired(() => { setUsuario(null); });
    return () => {
      vigente = false;
      onSessionExpired(null);
    };
  }, []);

  const ingresar = useCallback(async (email: string, clave: string) => {
    const nuevo = await api.ingresar({ email, clave });
    setUsuario(nuevo);
    return nuevo;
  }, []);

  const salir = useCallback(async () => {
    try {
      await api.salir();
    } finally {
      setUsuario(null);
    }
  }, []);

  const valor = useMemo(
    () => ({ usuario, cargando, ingresar, salir }),
    [usuario, cargando, ingresar, salir],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useSesion(): ContextoSesion {
  const contexto = useContext(Contexto);
  if (!contexto) throw new Error('useSesion se usa adentro de ProveedorSesion');
  return contexto;
}
